// ./src/config/trackingConstants.ts
//
// Configuration constants for the live GPS tracking engine.
// All values are developer-tuned — not user-configurable for v1.
//
// Organized by subsystem so each hook/module only imports what it needs.
//
// ── TEST MODE ──
// Set VITE_TRACKING_TEST_MODE=true in your .env.local to enable
// relaxed thresholds for indoor/walking testing. This lowers the
// distance and accuracy thresholds so that walking around a house
// generates enough points to exercise the full pipeline.

const IS_TEST_MODE = import.meta.env.VITE_TRACKING_TEST_MODE === "true";

// ============================================
// GPS Watcher (useGpsTracker)
// ============================================

/**
 * Discard GPS readings with accuracy worse than this (meters).
 * Test mode: 100m (indoor GPS can be very inaccurate)
 * Production: 30m
 */
export const GPS_ACCURACY_THRESHOLD = IS_TEST_MODE ? 100 : 30;

/** Geolocation API options */
export const GPS_WATCHER_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  /** Don't use cached positions */
  maximumAge: 0,
  /** Timeout for a single position request (ms) */
  timeout: 10_000,
};

// ============================================
// Point Filter (useTrackingFilter)
// ============================================

/**
 * Smart filter thresholds. A raw GPS point is kept if ANY condition is met.
 * These are fixed for v1; adaptive filtering (speed-based) is a future enhancement.
 *
 * Test mode uses much lower thresholds:
 * - 2m distance (instead of 20m) — a few steps triggers a point
 * - 5° heading (instead of 12°) — slight turns are captured
 * - 0.5 m/s speed change (instead of 3) — walking pace changes count
 * - 5 seconds max interval (instead of 30) — frequent keepalive points
 */
export const FILTER_THRESHOLDS = IS_TEST_MODE
  ? {
    DISTANCE_M: 2,
    HEADING_DEG: 5,
    SPEED_MPS: 0.5,
    MAX_INTERVAL_MS: 5_000,
  } as const
  : {
    /** Keep point if distance from last kept point exceeds this (meters) */
    DISTANCE_M: 20,
    /** Keep point if heading changed more than this since last kept point (degrees) */
    HEADING_DEG: 12,
    /** Keep point if speed changed more than this since last kept point (m/s ~ 10 km/h) */
    SPEED_MPS: 3,
    /** Keep point if this much time has elapsed since last kept point (ms) */
    MAX_INTERVAL_MS: 30_000,
  } as const;

// ============================================
// Batch Uploader (useTrackingUploader)
// ============================================

/**
 * How often to flush the buffer to the server (ms).
 * Test mode: 5 seconds (see uploads happen faster)
 */
export const FLUSH_INTERVAL_MS = IS_TEST_MODE ? 5_000 : 10_000;

/** Flush early if the buffer reaches this size (points) */
export const MAX_BUFFER_SIZE = IS_TEST_MODE ? 5 : 20;

/** Maximum points per server batch (must match backend validator limit of 50) */
export const MAX_BATCH_SIZE = 50;

/**
 * Backoff schedule for failed uploads (ms).
 * After each consecutive failure, use the next interval.
 * Stays at the last value until success resets to index 0.
 */
export const UPLOAD_BACKOFF_SCHEDULE = [10_000, 20_000, 30_000] as const;

// ============================================
// Session Persistence
// ============================================

/**
 * How often to persist session state to IndexedDB (ms).
 * This is separate from the upload flush interval — it saves
 * the local counters (seq, gpsDistance, duration) for crash recovery.
 * Piggybacks on the flush cycle, so this is effectively the same
 * as FLUSH_INTERVAL_MS but defined separately for clarity.
 */
export const SESSION_PERSIST_INTERVAL_MS = IS_TEST_MODE ? 5_000 : 10_000;

// ============================================
// Duration Timer
// ============================================

/** How often to update the displayed active duration (ms) */
export const DURATION_TICK_INTERVAL_MS = 1_000;

// ============================================
// Test Mode Logging
// ============================================

if (IS_TEST_MODE) {
  console.warn(
    "[Tracking] TEST MODE ENABLED — using relaxed thresholds for indoor testing.\n" +
    "  GPS accuracy threshold: 100m\n" +
    "  Distance filter: 2m\n" +
    "  Heading filter: 5°\n" +
    "  Speed filter: 0.5 m/s\n" +
    "  Time filter: 5s\n" +
    "  Flush interval: 5s\n" +
    "  Buffer flush size: 5 points\n" +
    "Remove VITE_TRACKING_TEST_MODE from .env.local for production thresholds."
  );
}